import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AccountGroup, AccountPlatform, AccountType, BusinessUnit, DescribableAccount } from '../../../domain/account.domain';

export enum AccountsDescriptionType {
  SHORT = 'short',
  NORMAL = 'normal',
  NO_NICKNAME = 'noNickname'
}

@Injectable({
  providedIn: 'root'
})
export class AccountsDescriptionService {
  private readonly translateService = inject(TranslateService);

  /**
   * @ngdoc method
   * @name setDescriptions
   * @methodOf com.td.brk.wb.core.components.accounts:tdWbAccountsDescriptionService
   * @description Will add accountTypeDescription and businessLineDescription to every account object
   *
   * @param accounts an array of account objects which contains  accountPlatform, description, tradingPlatform and nickName.
   * @param descriptionType 'short' | 'normal' then the result of the businessLine or LOB will be shortened
   * for display purposes. 'noNickname' value forces ignoring nickName even if account has it.
   * @param noDescriptionIfSameBusinessLine business line description will be empty if all accounts have the
   * same business line.
   *
   * @return a modified array of accounts where each value in the array has a resolve accountTypeDescription
   * and a businessLineDescription which may be empty if all accounts are from the same LOB, its own description otherwise,
   * or the nickname if present in any case
   */
  setDescriptions<T extends DescribableAccount>(accounts: Array<T>,
                                                descriptionType: AccountsDescriptionType = undefined,
                                                noDescriptionIfSameBusinessLine: boolean = false): Array<T> {
    if (accounts !== undefined && accounts.length > 0) {
      const suppressBusinessLineDescription = noDescriptionIfSameBusinessLine ? this.doAllSummariesHaveSameLOB(accounts) : false;
      accounts.forEach((account: T): void => {
        this.setAccountDescription(
          account,
          descriptionType,
          suppressBusinessLineDescription);
      });
    }
    return accounts;
  }

  /**
   * Similar to setDescriptions method, but accepts a single Account instance as a parameter.
   *
   * @param account the account to enrich with accountTypeDescription and businessLineDescription
   * @param descriptionType optional param; can be one of: 'short' | 'normal'
   * @param suppressBusinessLineDescription optional param; set it to true and to skip businessLineDescription from being added
   * (businessLineDescription is still added and defaults to the account nickname if it exists)
   */
  setAccountDescription<T extends DescribableAccount>(account: T,
                                                      descriptionType: AccountsDescriptionType = undefined,
                                                      suppressBusinessLineDescription: boolean = false): void {
    this.setDescription(account,
      descriptionType,
      suppressBusinessLineDescription,
      this.getAccountType(account),
      account.description);
  }

  /**
   * @ngdoc method
   * @name setAccountGroupDescriptions
   * @methodOf com.td.brk.wb.core.components.accounts:tdWbAccountsDescriptionService
   * @description Will add accountTypeDescription and businessLineDescription to every account object
   *
   * @param accountGroups an array of account objects which contains accountPlatform, description, tradingPlatform and nickName.
   * @param descriptionType 'short' | 'normal' then the result of the businessLine or LOB will be shortened
   * for display purposes. 'noNickname' value forces ignoring nickName even if account has it.
   * @param noDescriptionIfSameBusinessLine business line description will be empty if all accounts have the
   * same business line.
   *
   * @return a modified array of accounts where each value in the array has a resolve accountTypeDescription
   * and a businessLineDescription which may be empty if all accounts are from the same LOB, its own description otherwise,
   * or the nickname if present in any case
   */
  setAccountGroupDescriptions(accountGroups: Array<AccountGroup>,
                              descriptionType: AccountsDescriptionType = undefined): Array<AccountGroup> {
    if (accountGroups !== undefined && accountGroups.length > 0) {
      accountGroups.forEach((accountGroup: AccountGroup): void => {
        this.setDescription(accountGroup,
          descriptionType,
          false,
          AccountType.UNKNOWN);
      });
    }
    return accountGroups;
  }

  /**
   * Returns a locale-specific description (e.g. 'Marge à découvert') for a given account or group.
   *
   * @param accountType the account to find the description for
   * @param description the value to default to
   */
  getAccountDescription(accountOrGroup: DescribableAccount): string {
    const accountType: AccountType = this.getAccountType(accountOrGroup);
    return this.getTypeDescription(accountType, accountOrGroup.description);
  }

  /**
   * Returns a locale-specific description (e.g. 'Marge à découvert') for a given account type (e.g. 'MARGIN_SHORT').
   *
   * @param accountType the account type enum value to translate
   * @param description the value to default to
   */
  getTypeDescription(accountType: AccountType, description?: string): string {
    let typeDescription;
    if (accountType === undefined) {
      typeDescription = description;
    } else {
      typeDescription = this.translateService.instant('tdWb.components.accounts.descriptions.' + accountType);
    }
    return typeDescription;
  }

  private setDescription(accountOrGroup: DescribableAccount,
                         descriptionType: AccountsDescriptionType,
                         suppressBusinessLineDescription: boolean,
                         accountType: AccountType,
                         accountDescription: string = undefined): void {
    if (accountOrGroup.businessUnit === BusinessUnit.OFI || accountOrGroup.businessUnit === BusinessUnit.TDCT) {
      const nonTradingAccountDescKey = 'tdWb.components.accounts.nonTradingDivision.' + accountOrGroup.businessUnit;
      accountOrGroup.businessLineDescription = this.translateService.instant(nonTradingAccountDescKey);
      accountOrGroup.accountTypeDescription = '';
    } else {
      if (descriptionType !== AccountsDescriptionType.NO_NICKNAME && accountOrGroup.nickName !== undefined
        && accountOrGroup.nickName !== '') {
        accountOrGroup.businessLineDescription = accountOrGroup.nickName;
      } else {
        accountOrGroup.businessLineDescription = '';
        if (!suppressBusinessLineDescription && accountOrGroup.accountPlatform !== undefined &&
          accountOrGroup.accountPlatform !== AccountPlatform.UNKNOWN) {
          const businessLineKey = 'tdWb.components.accounts.accountPlatform.' + this.getDescriptionTypeKey(descriptionType) +
            '.' + accountOrGroup.accountPlatform;
          accountOrGroup.businessLineDescription = this.translateService.instant(businessLineKey);
        }
      }
      accountOrGroup.accountTypeDescription = this.getTypeDescription(accountType, accountDescription);
    }
  }

  private doAllSummariesHaveSameLOB(accounts: Array<{ accountPlatform: AccountPlatform }>): boolean {
    return !accounts.some((value: { accountPlatform: AccountPlatform }): boolean => accounts[0].accountPlatform !== value.accountPlatform);
  }

  private getDescriptionTypeKey(descriptionType: AccountsDescriptionType): AccountsDescriptionType {
    return descriptionType === undefined || descriptionType === AccountsDescriptionType.NO_NICKNAME ?
      AccountsDescriptionType.NORMAL : descriptionType;
  }

  private getAccountType(account: Pick<DescribableAccount, 'accountPlatform' | 'type' | 'description'>): AccountType {
    let accountType = AccountType.UNKNOWN;
    if (account.type !== undefined) {
      accountType = account.type.acctType;
    }
    return accountType;
  }
}
